// Contract Details panel — port of the source CRM's "Contract Details" tab.
// Two sections (Lead Information + Source Details), draft state with Save /
// Discard so the user can edit several fields and commit them in one PATCH
// rather than firing one per blur (which is what the drawer-header inline
// editors do). All fields here are also reachable via inline edit on the
// drawer hero / Edit Lead modal — this tab is the "everything in one place"
// view source-CRM users were used to.
//
// `sourceChannelId` from source CRM is intentionally omitted: there's no
// corresponding field on our Lead schema, and source CRM never wired it
// to anything meaningful either.

import React, { useEffect, useMemo, useState } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { CheckCircle2, Loader2, RotateCcw } from "lucide-react";
import { Button, Label, Input } from "../../atoms";
import MemberPicker from "../../molecules/MemberPicker";
import { useUpdateLead } from "../../hooks/useLeads";
import { useOrgCurrency } from "../../hooks/useOrgCurrency";
import { useMyCrmRole } from "../../hooks/useTeamMembers";

// Fields we control on this panel. Kept in one place so the diff/dirty
// check below stays accurate as we add columns.
const FIELDS = [
  "title", "brandName", "ownerId", "email", "phone",
  "value", "expectedCloseDate", "sourceChannel",
];

function draftFromLead(lead) {
  return {
    title: lead?.title || "",
    brandName: lead?.brandName || "",
    ownerId: lead?.ownerId || null,
    email: lead?.email || "",
    phone: lead?.phone || "",
    value: lead?.value ?? "",
    expectedCloseDate: lead?.expectedCloseDate || "",
    sourceChannel: lead?.sourceChannel || "",
  };
}

// Shallow per-field equality. expectedCloseDate stored as ISO string both
// sides, value as number/string — coerce to string before compare so a
// numeric 1000 vs string "1000" doesn't show up as dirty.
function isDirty(draft, lead) {
  if (!draft || !lead) return false;
  return FIELDS.some((f) => {
    const a = draft[f] == null ? "" : String(draft[f]);
    const b = lead[f] == null ? "" : String(lead[f]);
    return a !== b;
  });
}

const FormSection = ({ title, children }) => (
  <section style={{
    background: "var(--color-bg-surface)",
    border: "1px solid var(--color-border-subtle)",
    borderRadius: 8,
    padding: 18,
  }}>
    <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "var(--color-text-primary)", marginBottom: 14 }}>
      {title}
    </h3>
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 14,
    }}>
      {children}
    </div>
  </section>
);

const FieldCell = ({ label, htmlFor, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
    <Label htmlFor={htmlFor}>{label}</Label>
    {children}
  </div>
);

export default function ContractDetailsPanel({ leadId, lead }) {
  const update = useUpdateLead();
  const { currency: orgCurrency } = useOrgCurrency();
  const { isOwner } = useMyCrmRole();
  const [draft, setDraft] = useState(() => draftFromLead(lead));
  const [savedFlash, setSavedFlash] = useState(false);

  // Re-seed the draft when the lead doc changes from outside (refetch, server
  // push, inline-edit on the drawer header). useMemo + leadId so we don't
  // re-set on every parent re-render that hands the same lead back.
  const seedKey = useMemo(() => `${leadId}:${lead?.updatedAt || ""}`, [leadId, lead?.updatedAt]);
  useEffect(() => {
    setDraft(draftFromLead(lead));
    setSavedFlash(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey]);

  const dirty = isDirty(draft, lead);
  const currencySymbol = lead?.currency || orgCurrency || "USD";

  const setField = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setSavedFlash(false);
  };

  const onSave = async () => {
    if (!dirty) return;
    // Build a minimal patch — only fields that actually changed. Keeps the
    // server-side activity log focused (we don't want to log "title set to X"
    // when title didn't move).
    const patch = {};
    for (const f of FIELDS) {
      const a = draft[f] == null ? "" : draft[f];
      const b = lead[f] == null ? "" : lead[f];
      // value is numeric — cast before comparing and before sending.
      if (f === "value") {
        const next = a === "" ? null : Number(a);
        const prev = b === "" || b == null ? null : Number(b);
        if (next !== prev) patch.value = next;
        continue;
      }
      if (String(a) !== String(b)) patch[f] = a === "" ? null : a;
    }
    if (Object.keys(patch).length === 0) return;
    try {
      await update.mutateAsync({ id: leadId, patch });
      toast.success("Contract details saved");
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2200);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed to save");
    }
  };

  const onDiscard = () => {
    setDraft(draftFromLead(lead));
    setSavedFlash(false);
  };

  if (!lead) {
    return (
      <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
        Loading lead…
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 760 }}>
      <FormSection title="Lead information">
        <FieldCell label="Title" htmlFor="cd-title">
          <Input
            id="cd-title"
            value={draft.title}
            onChange={(e) => setField("title", e.target.value)}
            placeholder="Untitled lead"
          />
        </FieldCell>
        <FieldCell label="Brand / company" htmlFor="cd-brand">
          <Input
            id="cd-brand"
            value={draft.brandName}
            onChange={(e) => setField("brandName", e.target.value)}
            placeholder="Optional, unique per org"
          />
        </FieldCell>
        {/* Owner picker is gated on the owner role — matches the rest of the
            drawer's ownership UI. Non-owners see the current owner read-only. */}
        <FieldCell label="Owner">
          {isOwner ? (
            <MemberPicker
              value={draft.ownerId}
              onChange={(id) => setField("ownerId", id)}
              width="100%"
            />
          ) : (
            <div style={{
              fontSize: 13, color: "var(--color-text-secondary)",
              padding: "8px 10px", borderRadius: 8,
              background: "var(--color-bg-muted)",
              border: "1px solid var(--color-border-subtle)",
            }}>
              {lead.ownerName || lead.ownerId || "Unassigned"}
            </div>
          )}
        </FieldCell>
        <FieldCell label="Email" htmlFor="cd-email">
          <Input
            id="cd-email"
            type="email"
            value={draft.email}
            onChange={(e) => setField("email", e.target.value)}
            placeholder="example@email.com"
          />
        </FieldCell>
        <FieldCell label="Phone" htmlFor="cd-phone">
          <Input
            id="cd-phone"
            value={draft.phone}
            onChange={(e) => setField("phone", e.target.value)}
            placeholder="+1 (555) 000-0000"
          />
        </FieldCell>
        <FieldCell label={`Deal value (${currencySymbol})`} htmlFor="cd-value">
          <Input
            id="cd-value"
            type="number"
            inputMode="decimal"
            value={draft.value}
            onChange={(e) => setField("value", e.target.value)}
            placeholder="0.00"
          />
        </FieldCell>
        <FieldCell label="Expected close">
          <DatePicker
            value={draft.expectedCloseDate ? dayjs(draft.expectedCloseDate) : null}
            onChange={(d) => setField("expectedCloseDate", d ? d.toISOString() : "")}
            format="MMM D, YYYY"
            placeholder="Pick a date"
            style={{ width: "100%" }}
          />
        </FieldCell>
      </FormSection>

      <FormSection title="Source details">
        <FieldCell label="Source channel" htmlFor="cd-channel">
          <Input
            id="cd-channel"
            value={draft.sourceChannel}
            onChange={(e) => setField("sourceChannel", e.target.value)}
            placeholder="e.g. LinkedIn, Website"
          />
        </FieldCell>
      </FormSection>

      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        paddingTop: 4,
      }}>
        <Button
          variant="primary"
          size="md"
          onClick={onSave}
          disabled={!dirty || update.isPending}
          leftIcon={
            update.isPending ? <Loader2 size={13} className="animate-spin" />
              : savedFlash ? <CheckCircle2 size={13} />
                : null
          }
        >
          {update.isPending ? "Saving…" : savedFlash ? "Saved" : "Save changes"}
        </Button>
        <Button
          variant="secondary"
          size="md"
          onClick={onDiscard}
          disabled={!dirty || update.isPending}
          leftIcon={<RotateCcw size={13} />}
        >
          Discard
        </Button>
        {dirty && !update.isPending && (
          <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
            Unsaved changes
          </span>
        )}
      </div>
    </div>
  );
}
